/**
 * Quality (Screen A rates) — DEFERRED org rates beside REAL member quality-measures, each badged from
 * its OWN meta.provenance via ProvenanceBadge (never hand-tagged):
 *   1. Org rates — op_fact_quality_rate is ABSENT live, so the dashboard rollup and rate tiles serve an
 *      honest DEFERRED state ("Awaiting quality-rate model sync"), never a fabricated or zero rate.
 *   2. Member measures — REAL where backed: a ParticipantPicker drives GET
 *      /participants/:key/quality-measures rendered as a per-measure SkyDataGrid (Measure label / year /
 *      in-denominator / in-numerator / required-exclusion / numeric result / gap reason), each row
 *      provenance-badged, with client-side year + numerator/denominator filters over the member's set.
 * PACE suppression is honored throughout: the server never serializes the external rating program or the
 * raw measure code, and this screen never surfaces them. Measures are framed as internal PACE indicators
 * (NPA / DataPACE3) by name and domain only. Rows are never console.logged; each section isolates its
 * own loading/error.
 */
import { useMemo, useState, type ReactNode } from "react";
import {
  SkyBadge,
  SkyCard,
  SkyDataGrid,
  SkyDropdown,
  SkyText,
  SkyLoader,
  SkyMessageBar,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type {
  DataStatus,
  Provenance,
  ProvenanceFeed,
  QualityRate,
  QualityRatesDashboard,
  QualityMeasure,
} from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { useExemplarMember } from "../lib/useExemplarMember";
import { humanizeToken } from "../lib/copy";
import { ParticipantPicker } from "../components/ParticipantPicker";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

const AWAITING = "Awaiting quality-rate model sync";

const rateColumns: SkyDataGridColumn<QualityRate>[] = [
  { key: "org_scope", header: "Scope", width: "20%" },
  { key: "measure_code", header: "Indicator", width: "24%" },
  { key: "measurement_year", header: "Year", width: "14%" },
  {
    key: "rate_per_1000_mm",
    header: "Rate / 1000 MM",
    width: "20%",
    align: "right",
    renderCell: (_v, row) => (row.rate_per_1000_mm == null ? "" : String(row.rate_per_1000_mm)),
  },
  {
    key: "pct_value",
    header: "Percent",
    width: "14%",
    align: "right",
    renderCell: (_v, row) => (row.pct_value == null ? "" : `${row.pct_value}%`),
  },
];

/** Per-row provenance shape (each measure row carries its own data_status/feed/gap_reason → its badge). */
function rowProvenance(row: QualityMeasure): Provenance {
  return {
    dataStatus: (row.data_status as DataStatus) ?? "mock",
    provenanceFeed: (row.provenance_feed as ProvenanceFeed) ?? "none",
    gapReason: row.gap_reason ?? null,
    rowCount: 1,
  };
}

/** Yes/No status chip (status by color + LABEL). null → "Not recorded" (never a silent blank). */
function yesNoBadge(v: boolean | null, yesColor: "informative" | "success"): ReactNode {
  if (v == null) return "Not recorded";
  return (
    <SkyBadge color={v ? yesColor : "subtle"} appearance="tint" size="small">
      {v ? "Yes" : "No"}
    </SkyBadge>
  );
}

/**
 * L2 — rate_id sub-rate labeling. op_fact_quality_measure grain is member x measure x rate_id, so one
 * measure (e.g. Cervical Cancer Screening) legitimately produces several rows for a member (rate_id =
 * cotest / cytology / hrhpv). API == Lakebase 1:1 (NO double-count), but undifferentiated rows read as
 * separate screenings. The fix is DISPLAY-ONLY: keep every API row, but when a measure name repeats in the
 * member's set, label each row with its rate_id so it is unambiguous they are sub-rates of ONE measure (the
 * same rate_id de-dup lesson as care-gaps, applied to the quality display). PACE suppression is preserved:
 * rate_id is a clinical sub-rate label (cotest/cytology/hrhpv), NOT the raw HEDIS/STARS measure_code or
 * program — neither of which is ever bound here.
 */
function buildMeasureColumns(repeatedNames: Set<string>): SkyDataGridColumn<QualityMeasure>[] {
  return [
  {
    key: "name",
    header: "Measure",
    width: "22%",
    // PUBLIC label only (name + domain) — never the raw HEDIS/STARS code or program (PACE suppression).
    renderCell: (_v, row) => {
      const name = row.name ?? "Measure label pending";
      // Only annotate sub-rates when the SAME measure name appears more than once for this member — a
      // single-rate measure stays clean. The rate_id is the clinical sub-rate identity, never a HEDIS code.
      const isSubRate = repeatedNames.has(name) && row.rate_id != null;
      return (
        <div className="flex flex-col">
          <SkyText size={300} weight="medium">
            {name}
          </SkyText>
          {row.domain && <SkyText size={200}>{humanizeToken(row.domain)}</SkyText>}
          {isSubRate && (
            <SkyText size={200} color="muted">
              Sub-rate: {row.rate_id} (one of several sub-rates of this measure, not a separate screening)
            </SkyText>
          )}
        </div>
      );
    },
  },
  { key: "measurement_year", header: "Year", width: "9%" },
  {
    key: "in_denominator",
    header: "Denominator",
    width: "13%",
    renderCell: (_v, row) => yesNoBadge(row.in_denominator, "informative"),
  },
  {
    key: "in_numerator",
    header: "Numerator",
    width: "12%",
    renderCell: (_v, row) => yesNoBadge(row.in_numerator, "success"),
  },
  {
    key: "has_required_exclusion",
    header: "Exclusion",
    width: "10%",
    renderCell: (_v, row) => (row.has_required_exclusion == null ? "Not recorded" : row.has_required_exclusion ? "Yes" : "No"),
  },
  {
    key: "numeric_result",
    header: "Result",
    width: "9%",
    align: "right",
    renderCell: (_v, row) => (row.numeric_result == null ? "" : String(row.numeric_result)),
  },
  {
    key: "gap_reason",
    header: "Gap reason",
    width: "15%",
    renderCell: (_v, row) => row.gap_reason ?? "",
  },
  {
    key: "provenance",
    header: "Provenance",
    width: "10%",
    renderCell: (_v, row) => <ProvenanceBadge meta={rowProvenance(row)} />,
  },
  ];
}

/** Org rates section (DEFERRED — op_fact_quality_rate absent). */
function OrgRatesSection() {
  const dashboard = usePage<QualityRatesDashboard>("/api/v1/quality-rates/dashboard");
  const rates = usePage<QualityRate[]>("/api/v1/quality-rates");
  return (
    <SkyCard
      header={{
        title: "Organization quality rates",
        description: "Org-grain indicator rates rollup",
        action: <ProvenanceBadge meta={rates.meta?.provenance} />,
      }}
    >
      {(dashboard.loading || rates.loading) && (
        <SkyLoader size="small" label="Loading quality rates" />
      )}
      {rates.error && (
        <SkyMessageBar intent="error" title="Could not load quality rates" message={rates.error} />
      )}
      {!rates.loading && !rates.error && (
        <div className="flex flex-col gap-3">
          <SkyText size={300}>
            {dashboard.data ? `${dashboard.data.measure_count} indicator(s) tracked.` : "Indicator rollup awaiting sync."}
          </SkyText>
          <SkyDataGrid<QualityRate>
            data={rates.data ?? []}
            columns={rateColumns}
            getRowId={(_r, i) => String(i)}
            emptyMessage={AWAITING}
          />
        </div>
      )}
    </SkyCard>
  );
}

/** Member measures section (REAL where backed) with client-side year + numerator/denominator filters. */
function MemberMeasuresSection({ memberKey }: { memberKey: string | null }) {
  const { data, meta, loading, error } = usePage<QualityMeasure[]>(
    memberKey ? `/api/v1/participants/${memberKey}/quality-measures` : null,
  );
  const rows = useMemo(() => data ?? [], [data]);

  const [year, setYear] = useState("");
  const [denom, setDenom] = useState("");
  const [numer, setNumer] = useState("");

  const years = useMemo(
    () =>
      [...new Set(rows.map((r) => r.measurement_year).filter((y): y is number => y != null))].sort(
        (a, b) => b - a,
      ),
    [rows],
  );

  // L2 — names that occur more than once in the member's set are multi-sub-rate measures (member x measure
  // x rate_id grain). Computed over the FULL member set (not the filtered view) so the sub-rate labeling is
  // stable under client-side filtering. Drives buildMeasureColumns so repeated rows are labeled by rate_id.
  const repeatedNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const n = r.name ?? "Measure label pending";
      counts.set(n, (counts.get(n) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, c]) => c > 1).map(([n]) => n));
  }, [rows]);
  const hasSubRates = repeatedNames.size > 0;
  const columns = useMemo(() => buildMeasureColumns(repeatedNames), [repeatedNames]);

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (year && String(r.measurement_year) !== year) return false;
        if (denom && (denom === "yes") !== Boolean(r.in_denominator)) return false;
        if (numer && (numer === "yes") !== Boolean(r.in_numerator)) return false;
        return true;
      }),
    [rows, year, denom, numer],
  );

  const yesNoOptions = [
    { value: "yes", text: "Yes" },
    { value: "no", text: "No" },
  ];

  return (
    <SkyCard
      header={{
        title: "Member quality measures",
        description: "Per-participant numerator / denominator / exclusion status, by indicator name and domain only.",
        action: memberKey ? <ProvenanceBadge meta={meta?.provenance} /> : undefined,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading member measures" />}
      {error && (
        <SkyMessageBar intent="error" title="Could not load member measures" message={error} />
      )}
      {!loading && !error && memberKey && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40 max-w-full">
              <SkyDropdown
                label="Measurement year"
                options={years.map((y) => ({ value: String(y), text: String(y) }))}
                placeholder="All years"
                clearable
                selectedOptions={year ? [year] : []}
                onOptionSelect={(v) => setYear(v[0] ?? "")}
              />
            </div>
            <div className="w-40 max-w-full">
              <SkyDropdown
                label="In denominator"
                options={yesNoOptions}
                placeholder="All"
                clearable
                selectedOptions={denom ? [denom] : []}
                onOptionSelect={(v) => setDenom(v[0] ?? "")}
              />
            </div>
            <div className="w-40 max-w-full">
              <SkyDropdown
                label="In numerator"
                options={yesNoOptions}
                placeholder="All"
                clearable
                selectedOptions={numer ? [numer] : []}
                onOptionSelect={(v) => setNumer(v[0] ?? "")}
              />
            </div>
          </div>
          {/* L2 — explain the member x measure x rate_id grain so repeated measure names do not read as
              duplicate screenings. Only shown when this member actually has a multi-sub-rate measure. */}
          {hasSubRates && (
            <SkyText size={200} color="muted">
              Some measures report several sub-rates (rate_id, e.g. cotest / cytology / hrHPV for Cervical
              Cancer Screening). Rows sharing a measure name are sub-rates of ONE measure, labeled by their
              rate_id below, not separate screenings.
            </SkyText>
          )}
          <SkyText size={200}>
            {filtered.length} of {rows.length} measure rows
          </SkyText>
          <SkyDataGrid<QualityMeasure>
            data={filtered}
            columns={columns}
            getRowId={(_r, i) => String(i)}
            emptyMessage="No measures match the current filters"
          />
        </div>
      )}
      {!memberKey && <SkyText size={300}>Select a participant to view their measures.</SkyText>}
    </SkyCard>
  );
}

export function Quality() {
  // Default the member-measures section to the conditions-richest REAL member (member quality-measures
  // have no dedicated exemplar table; the clinically-richest member is the most useful default) so the
  // section renders REAL data without a manual pick. The ParticipantPicker still lets the user change.
  const [picked, setPicked] = useState<string | null>(null);
  const { key: defaultKey, error: exemplarError } = useExemplarMember("conditions");
  const key = picked ?? defaultKey;

  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "quality", label: "Quality" }]}
        title="Quality"
        description="Internal PACE quality indicators (NPA / DataPACE3 framing). Organization rates are deferred until their model syncs; member-level measures render live where backed. Indicators are shown by name and domain only, never an external rating program or raw code."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        <SkyMessageBar
          intent="info"
          title="Awaiting quality-rate model sync"
          message="The organization quality-rate model has not been delivered. Org rates below are DEFERRED, never a fabricated or zero rate. Member-level measures are live."
        />
        <OrgRatesSection />
        <div className="w-80">
          <ParticipantPicker value={key ?? undefined} onSelect={setPicked} />
        </div>
        {/* The default-member lookup failed and the user has not picked one yet: surface an honest bar
            (the picker above is the way out) instead of silently defaulting to no member. */}
        {exemplarError && !key && (
          <SkyMessageBar
            intent="warning"
            title="Could not load a default member"
            message="Could not load a default member - search above to choose a participant."
          />
        )}
        {/* key on the member forces a remount when the member changes, so the section's local
            year / denominator / numerator filters RESET instead of silently carrying stale
            filters onto the next member (which would read as a misleading "0 rows"). */}
        <MemberMeasuresSection key={key ?? "none"} memberKey={key} />
      </section>
      <PageFooter />
    </>
  );
}
